// Reine Suchhilfen für SearchSelect (≈ 500 Kontakte, README §9).

/** Klein, ohne Akzente, ß → ss, Leerraum zusammengefasst. „Müller“ wird über „muller“ gefunden, nicht über „mueller“ – bewusst schlicht. */
export function normalisiereSuche(text: string): string {
  return text
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface SucheOption {
  id: string;
  label: string;
  sub?: string;
  /** z. B. 'mieter' | 'eigentuemer' | 'dienstleister' */
  typ?: string;
  /** zusätzlich durchsuchter Text (z. B. Straße, Ort, Objektnummer) */
  suchtext?: string;
}

/**
 * Lokale Suche: alle Wörter der Anfrage müssen in Label oder Untertitel vorkommen.
 * Treffer am Wortanfang des Labels zuerst. Höchstens `max` Ergebnisse.
 */
export function filtereOptionen<T extends SucheOption>(optionen: readonly T[], anfrage: string, typ: string | null, max: number): T[] {
  const q = normalisiereSuche(anfrage);
  if (!q) return [];
  const woerter = q.split(' ');
  const treffer: { o: T; rang: number }[] = [];
  for (const o of optionen) {
    if (typ && o.typ !== typ) continue;
    const label = normalisiereSuche(o.label);
    const heu = `${label} ${normalisiereSuche(o.sub ?? '')} ${normalisiereSuche(o.suchtext ?? '')}`;
    if (!woerter.every(w => heu.includes(w))) continue;
    const rang = label.startsWith(q) ? 0 : label.split(' ').some(t => t.startsWith(woerter[0] ?? '')) ? 1 : 2;
    treffer.push({ o, rang });
  }
  treffer.sort((a, b) => a.rang - b.rang || a.o.label.localeCompare(b.o.label, 'de'));
  return treffer.slice(0, max).map(t => t.o);
}

/**
 * Filter für Auswahllisten, deren Reihenfolge erhalten bleiben soll (z. B. Objekte nach Nummer):
 * alle Wörter müssen in Label, Untertitel oder Suchtext vorkommen; leere Anfrage = alle.
 */
export function filtereInReihenfolge<T extends SucheOption>(optionen: readonly T[], anfrage: string): T[] {
  const q = normalisiereSuche(anfrage);
  if (!q) return [...optionen];
  const woerter = q.split(' ');
  return optionen.filter(o => {
    const heu = `${normalisiereSuche(o.label)} ${normalisiereSuche(o.sub ?? '')} ${normalisiereSuche(o.suchtext ?? '')}`;
    return woerter.every(w => heu.includes(w));
  });
}

export interface Textteil { text: string; treffer: boolean }

/** Zerlegt einen Text in Treffer-/Nicht-Treffer-Teile (für Fettdruck im Dropdown). */
export function markiereTreffer(text: string, anfrage: string): Textteil[] {
  const q = normalisiereSuche(anfrage);
  if (!q) return [{ text, treffer: false }];
  const woerter = q.split(' ').filter(Boolean);
  // Normalisierung ändert die Länge nur bei ß und Akzentzeichen; wir vergleichen Zeichen für Zeichen.
  const zeichen = Array.from(text);
  const norm = zeichen.map(z => normalisiereSuche(z) || z);
  const markiert = new Array<boolean>(zeichen.length).fill(false);
  const flach = norm.join('');
  // Startindex im flachen String → Zeichenindex
  const startVon: number[] = [];
  let pos = 0;
  norm.forEach((n, i) => { for (let k = 0; k < n.length; k++) startVon[pos + k] = i; pos += n.length; });
  for (const w of woerter) {
    let ab = flach.indexOf(w);
    while (ab !== -1) {
      const von = startVon[ab] ?? 0;
      const bis = startVon[ab + w.length - 1] ?? von;
      for (let i = von; i <= bis; i++) markiert[i] = true;
      ab = flach.indexOf(w, ab + w.length);
    }
  }
  const teile: Textteil[] = [];
  zeichen.forEach((z, i) => {
    const t = markiert[i] ?? false;
    const letzter = teile[teile.length - 1];
    if (letzter && letzter.treffer === t) letzter.text += z;
    else teile.push({ text: z, treffer: t });
  });
  return teile;
}
